import { useState, useMemo } from 'react';
import { FIELD_TYPES } from '../helpers/planFieldConfigs';
import { isFieldVisible } from '../helpers/fieldValidation';

const CustomPlanFields = ({ config, values, onChange, errors: externalErrors = {} }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Filter visible fields based on conditions
  const visibleFields = useMemo(() => {
    if (!config?.fields) return [];
    return config.fields.filter((field) => isFieldVisible(field, values));
  }, [config?.fields, values]);

  if (!config?.fields) return null;

  // Only show external errors (from form submission)
  const getFieldError = (field) => {
    return externalErrors[field.name] || null;
  };

  const hasError = (field) => !!externalErrors[field.name];

  const styles = {
    container: {
      margin: '0.75em 0',
      borderRadius: '8px',
      border: '1px solid #e0e0e0',
      overflow: 'hidden',
      backgroundColor: '#fff',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 16px',
      backgroundColor: '#f8f9fa',
      cursor: 'pointer',
      userSelect: 'none',
      borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none',
      transition: 'background-color 0.15s ease',
    },
    title: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#242479',
      letterSpacing: '0.5px',
      margin: 0,
    },
    chevron: {
      fontSize: '0.7rem',
      color: '#242479',
      transition: 'transform 0.2s ease',
      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    },
    content: {
      maxHeight: isExpanded ? '2000px' : '0',
      overflow: 'hidden',
      transition: 'max-height 0.3s ease-in-out',
      backgroundColor: '#fff',
    },
    innerContent: {
      padding: isExpanded ? '16px' : '0 16px',
    },
    fieldContainer: {
      marginBottom: '16px',
    },
    label: {
      display: 'block',
      fontSize: '0.9rem',
      fontWeight: 300,
      color: '#000',
      marginBottom: '6px',
    },
    required: {
      color: '#dc3545',
      marginLeft: '3px',
      fontWeight: 400,
    },
    input: {
      width: '100%',
      boxSizing: 'border-box',
      padding: '10px 12px',
      fontSize: '0.9rem',
      fontWeight: 300,
      color: '#000',
      backgroundColor: '#fff',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      outline: 'none',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    },
    inputError: {
      borderColor: '#dc3545',
    },
    inputFocus: {
      borderColor: '#242479',
      boxShadow: '0 0 0 3px rgba(36, 36, 121, 0.1)',
    },
    select: {
      width: '100%',
      boxSizing: 'border-box',
      padding: '10px 12px',
      fontSize: '0.9rem',
      fontWeight: 300,
      color: '#000',
      backgroundColor: '#fff',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      outline: 'none',
      cursor: 'pointer',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 12px center',
      paddingRight: '36px',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    },
    textarea: {
      width: '100%',
      boxSizing: 'border-box',
      padding: '10px 12px',
      fontSize: '0.9rem',
      fontWeight: 300,
      color: '#000',
      backgroundColor: '#fff',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      outline: 'none',
      resize: 'vertical',
      minHeight: '80px',
      fontFamily: 'inherit',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    },
    hint: {
      display: 'block',
      fontSize: '0.8rem',
      fontWeight: 300,
      color: '#6b7280',
      marginTop: '4px',
    },
    error: {
      display: 'block',
      fontSize: '0.8rem',
      fontWeight: 400,
      color: '#dc3545',
      marginTop: '4px',
    },
  };

  const getInputStyle = (field) => ({
    ...styles.input,
    ...(hasError(field) ? styles.inputError : {}),
  });

  const getSelectStyle = (field) => ({
    ...styles.select,
    ...(hasError(field) ? styles.inputError : {}),
  });

  const getTextareaStyle = (field) => ({
    ...styles.textarea,
    ...(hasError(field) ? styles.inputError : {}),
  });

  const renderField = (field) => {
    const value = values[field.name] ?? field.defaultValue ?? '';
    const commonProps = {
      name: field.name,
      value,
      onChange,
      required: field.required,
    };

    switch (field.type) {
      case FIELD_TYPES.TEXT:
        return (
          <input
            type="text"
            {...commonProps}
            style={getInputStyle(field)}
            placeholder={field.placeholder}
            maxLength={field.validation?.maxLength}
          />
        );

      case FIELD_TYPES.PHONE:
        return (
          <input
            type="tel"
            {...commonProps}
            style={getInputStyle(field)}
            pattern="[0-9]{10}"
            placeholder={field.placeholder || '1234567890'}
          />
        );

      case FIELD_TYPES.NUMBER:
        return (
          <input
            type="number"
            {...commonProps}
            style={getInputStyle(field)}
            placeholder={field.placeholder}
            min={field.validation?.min}
            max={field.validation?.max}
          />
        );

      case FIELD_TYPES.DATE:
        return (
          <input
            type="date"
            {...commonProps}
            style={getInputStyle(field)}
          />
        );

      case FIELD_TYPES.TEXTAREA:
        return (
          <textarea
            {...commonProps}
            style={getTextareaStyle(field)}
            placeholder={field.placeholder}
            rows={field.rows || 3}
          />
        );

      case FIELD_TYPES.SELECT: {
        const options = field.options || [];
        // Custom onChange to preserve original value types (e.g., booleans)
        const handleSelectChange = (e) => {
          const selectedStringValue = e.target.value;
          // Find the original option to get the actual value (preserving type)
          const selectedOption = options.find((opt) => {
            const optValue = typeof opt === 'string' ? opt : opt.value;
            return String(optValue) === selectedStringValue;
          });
          const actualValue = selectedOption
            ? (typeof selectedOption === 'string' ? selectedOption : selectedOption.value)
            : selectedStringValue;
          // Create a synthetic event with the correct value
          onChange({
            target: {
              name: field.name,
              value: actualValue,
            },
          });
        };
        return (
          <select
            name={field.name}
            value={String(value)}
            onChange={handleSelectChange}
            required={field.required}
            style={getSelectStyle(field)}
          >
            <option value="">{field.placeholder || 'Select'}</option>
            {options.map((opt, idx) => {
              const optValue = typeof opt === 'string' ? opt : opt.value;
              const optLabel = typeof opt === 'string' ? opt : opt.label;
              return (
                <option key={String(optValue) || idx} value={String(optValue)}>
                  {optLabel}
                </option>
              );
            })}
          </select>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      <div
        style={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <span style={styles.title}>Case Specific Details</span>
        <span style={styles.chevron}>▼</span>
      </div>
      <div style={styles.content}>
        <div style={styles.innerContent}>
          {visibleFields.map((field) => {
            const error = getFieldError(field);
            return (
              <div key={field.name} style={styles.fieldContainer}>
                <label style={styles.label}>
                  {field.label}
                  {field.required && <span style={styles.required}>*</span>}
                </label>
                {renderField(field)}
                {field.hint && !error && <small style={styles.hint}>{field.hint}</small>}
                {error && <small style={styles.error}>{error}</small>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CustomPlanFields;
